import { FC, ReactNode } from 'react';
import { format } from 'date-fns';
import { ProgressSpinner, UserAvatar, YellowWarningIcon, confirmDialog, useGlobalToast } from '@repo/atomic-components';
import { useAzureFileUpload } from '@repo/file-uploader';
import { useAppSelector, selectCaseIdData, selectCaseMetadata, selectFullProductType } from '@/store';
import { openNewTab } from '@/utils';
import {
  AppRoutes,
  AccessControlAction,
  CANCEL_AI_PROCESSING_CONFIRM,
  CASE_FAILED_STATUSES,
  CASE_PROCESSING_STATUSES,
} from '@/constants';
import {
  api,
  CASE_METADATA_TAG,
  CASE_STATUS_TAG,
  useGetCaseQuery,
  useLazyGetCaseAllegationDownloadQuery,
  useLazyGetCaseQuery,
  useLazyPostCaseStopPaginationQuery,
} from '@/api';
import { useAccessControl } from '@/hooks';
import { CaseStatus } from '@/ui/pages/Case/CaseItemPage/utils';
import { CaseBackgroundSection, CaseBackgroundDocItem } from '@/ui/components/CaseBackgroundSection';
import { WarningConfirmationDialog } from '@/ui/components/WarningConfirmationDialog';

// Single read-only field row. Title + value share the form-sidebar
// vertical rhythm (14/20 label in gray-200, 14/20 value in
// fletchers-black-900). Default label→value gap is tight (2px) so the
// value reads as a continuation of the label; rows with avatars set
// `looseGap` to 8px so the label has room to breathe above the chip.
const InfoField = ({ label, children, looseGap }: { label: string; children: ReactNode; looseGap?: boolean }) => (
  <div className={`flex flex-col ${looseGap ? 'gap-2' : 'gap-0.5'}`}>
    <span className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]">{label}</span>
    <span className="text-fletchers-black-900 block text-[14px]/[20px] break-words whitespace-pre-line">
      {children}
    </span>
  </div>
);

// Placeholder rendered for null / missing field values. Italics + gray-200
// keeps it visually distinct from real data while leaving the row in
// place so the field stack stays consistent case-to-case.
const EmptyValue = () => <span className="text-fletchers-gray-200 italic">Not set</span>;

// Robust date formatter — the BE has historically returned dates in
// multiple formats ("May 29, 2025", "2025-05-29", "11:50:24 01 Jun 2026"),
// so any value that doesn't `Date.parse` cleanly falls back to the raw
// string rather than rendering "Invalid Date".
const formatDate = (value?: string | null, pattern = 'd MMM y'): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return value;
  return format(parsed, pattern);
};

export const CaseInfoSidebar: FC = () => {
  // Case metadata + product type come from the store (the layout's
  // `triggerGetCase` populates them on mount). `additionalInfo` isn't
  // mirrored to the store, so we read it directly from the RTK Query
  // cache — `useGetCaseQuery` subscribes to the same cache entry the
  // layout's lazy fetch wrote, so no extra request is made.
  const caseId = useAppSelector(selectCaseIdData);
  const caseMetadata = useAppSelector(selectCaseMetadata);
  const fullProductType = useAppSelector(selectFullProductType);
  const { data: fullCaseData } = useGetCaseQuery({ caseId: caseId ?? '' }, { skip: !caseId });
  const additionalInfo = fullCaseData?.additionalInfo;

  // Case background documents — sourced from the full-case fetch so the
  // tiles read the same list the Create/Edit sidebar renders. Empty array
  // when the case has none, in which case we hide the whole section
  // below rather than showing an empty grid + divider.
  const allegationDocuments = fullCaseData?.info?.allegationDocuments ?? [];
  const caseBackgroundDocs: CaseBackgroundDocItem[] = allegationDocuments.map((d) => ({
    id: d.allegationDocumentId,
    name: d.allegationDocumentName,
  }));

  // Download flow — mirrors CreateEditCaseSidebar's handler. Case-scoped
  // signed-URL fetch, find the matching node, hand it to the shared Azure
  // downloader. Toast on failure so a missing doc or expired token doesn't
  // fail silently.
  const globalToastRef = useGlobalToast();
  const { downloadFile } = useAzureFileUpload();
  const [triggerGetCaseAllegationDownload] = useLazyGetCaseAllegationDownloadQuery();
  const handleAllegationDownload = (allegationDocumentId: string) => {
    if (!caseId) return;
    triggerGetCaseAllegationDownload({ caseId })
      .unwrap()
      .then((response) => {
        const match = response.nodes.find((n) => n.allegationDocumentId === allegationDocumentId);
        if (!match) {
          globalToastRef.current?.show({
            severity: 'error',
            summary: 'Download unavailable',
            detail: 'This document could not be located. Please refresh the page and try again.',
            life: 5000,
          });
          return;
        }
        downloadFile(match.allegationDocumentName, match.downloadPath, match.downloadToken);
      })
      .catch(() => {
        globalToastRef.current?.show({
          severity: 'error',
          summary: 'Download failed',
          detail: 'The document could not be downloaded. Please try again.',
          life: 5000,
        });
      });
  };

  // Cancel-AI plumbing. Mirrors the logic that used to live on the
  // page in StopAIProcessingButton — moved here so the cancel control
  // sits adjacent to the status it interrupts.
  const isStopPaginationAllowed = useAccessControl(AccessControlAction.START_PAGINATION_AI_PROCESSING);
  const [triggerPostCaseStopPagination, { isLoading: isStoppingPagination }] = useLazyPostCaseStopPaginationQuery();
  const [triggerRefetchCase] = useLazyGetCaseQuery();
  const isCancellable =
    caseMetadata?.status === CaseStatus.AI_PAGINATION_PROCESSING ||
    caseMetadata?.status === CaseStatus.AI_TIMELINE_PROCESSING;
  const handleStopAiClick = async () => {
    if (!caseId) return;
    const confirmed = await confirmDialog({ id: CANCEL_AI_PROCESSING_CONFIRM, caseId });
    if (!confirmed) return;
    triggerPostCaseStopPagination({ caseId })
      .unwrap()
      .then(() => {
        api.util.invalidateTags([
          { type: CASE_STATUS_TAG, id: caseId },
          { type: CASE_METADATA_TAG, id: caseId },
        ]);
        triggerRefetchCase({ caseId });
      });
  };

  return (
    <>
      {/* CaseTemplate lifts the page header to z-[1102] so the
          app's top nav stays in front of the create/edit case
          sidebar overlay (z-[1000]). Without the mask override the
          confirm dialog's mask would sit at z-[1101] (PrimeReact
          default) — i.e. behind the header — leaving the nav looking
          un-dimmed while the dialog itself dimmed everything below
          it. Bumping to z-[1200] puts the mask *above* the header
          so the whole viewport dims uniformly. */}
      <WarningConfirmationDialog
        id={CANCEL_AI_PROCESSING_CONFIRM}
        bodyText="AI processing will be abandoned for this case."
        confirmLabel="Abandon"
        pt={{ mask: { className: '!z-[1200]' } }}
      />

      {/* Padding + gaps mirror the form sidebars: `pl-6 pr-6 pt-6 pb-4`
          outer, `gap-6` between sections, `gap-2` inside a section.
          Title is 20/28 weight 600 (matches "Create case" / "Edit
          case"); the product-type subtitle below sits a tighter 4px
          under it. */}
      <div className="flex h-full flex-col gap-6 overflow-y-auto bg-white px-6 pt-6 pb-4">
        <div className="flex flex-shrink-0 flex-col gap-1">
          <h2 className="overflow-hidden text-[20px]/[28px] font-[600] text-ellipsis whitespace-nowrap">
            {caseMetadata?.caseReference ?? 'ID'}
          </h2>
          {(caseMetadata?.gRef || caseMetadata?.isUrgent) && (
            <div className="flex items-center gap-2">
              {caseMetadata?.gRef && (
                <span className="text-fletchers-gray-200 overflow-hidden text-[14px]/[20px] font-[400] text-ellipsis whitespace-nowrap">
                  {caseMetadata.gRef}
                </span>
              )}
              {caseMetadata?.isUrgent && (
                // 20px-tall pastel-red pill, same component as the urgent
                // flag on the case-list table. No InfoField label — the
                // pill is self-describing.
                <span
                  className="bg-fletchers-red-50 text-fletchers-red-900 inline-flex w-fit items-center rounded-[4px] px-2 text-[12px] font-semibold tracking-wide"
                  style={{ height: 20 }}
                >
                  URGENT
                </span>
              )}
            </div>
          )}
        </div>

        <hr className="border-fletchers-gray-110" />

        {additionalInfo?.clientName && (
          <InfoField label="Client">
            <span>
              {additionalInfo.clientName}
              {additionalInfo.clientDob && (
                <span className="text-fletchers-gray-200"> · {formatDate(additionalInfo.clientDob)}</span>
              )}
            </span>
          </InfoField>
        )}

        {caseMetadata?.instructingCompanyName && (
          <InfoField label="Instructing company">{caseMetadata.instructingCompanyName}</InfoField>
        )}

        <InfoField label="Product type">{fullProductType || <EmptyValue />}</InfoField>

        {caseMetadata?.dueDate && <InfoField label="Due date">{formatDate(caseMetadata.dueDate)}</InfoField>}

        <hr className="border-fletchers-gray-110" />

        <InfoField label="Status">
          {caseMetadata?.status ? (
            (() => {
              // Mirrors the case-list table column: processing statuses
              // render a spinner, failed statuses render a yellow warning
              // icon, both sit 8px to the left of the status text.
              const status = caseMetadata.status;
              const isProcessing = CASE_PROCESSING_STATUSES.includes(
                status as (typeof CASE_PROCESSING_STATUSES)[number],
              );
              const isFailed = CASE_FAILED_STATUSES.includes(status as (typeof CASE_FAILED_STATUSES)[number]);
              return (
                <span className="flex flex-col gap-0.5">
                  <span className="flex items-center gap-2">
                    <span className="flex items-center gap-1">
                      {isProcessing && (
                        <ProgressSpinner
                          style={{ width: '16px', height: '16px' }}
                          strokeWidth="4"
                          animationDuration="1.5s"
                          // The shared theme applies `mx-auto` + a
                          // `before:pt-full` aspect-ratio spacer on the
                          // root, intended for the centered loading-page
                          // spinner. In an inline-with-text context that
                          // adds horizontal margin we don't want — strip
                          // both so the 16px spinner sits flush against
                          // the 4px gap.
                          pt={{ root: { className: '!m-0 !p-0 !before:hidden' } }}
                        />
                      )}
                      {isFailed && <YellowWarningIcon width={16} height={16} />}
                      <span>{status}</span>
                    </span>
                    {/* Cancel AI processing — same 24px red chip as the
                      assignee-clear button. Only rendered for users
                      with the START_PAGINATION_AI_PROCESSING permission
                      and only when the case is in a cancellable AI
                      status (pagination/timeline processing). */}
                    {isStopPaginationAllowed && isCancellable && (
                      <button
                        type="button"
                        aria-label={isStoppingPagination ? 'Cancelling AI processing' : 'Cancel AI processing'}
                        title={isStoppingPagination ? 'Cancelling…' : 'Cancel AI processing'}
                        onClick={handleStopAiClick}
                        disabled={isStoppingPagination}
                        className="border-fletchers-red-500 hover:bg-fletchers-red-50 flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md border bg-white disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {/* 10×10 red stop-square — same visual weight as
                          the CloseIcon used elsewhere. */}
                        <span className="bg-fletchers-red-500 block h-[10px] w-[10px] rounded-[1px]" />
                      </button>
                    )}
                  </span>
                  {caseMetadata.statusUpdated && (
                    <span className="text-fletchers-gray-200 text-[12px]/[16px]">
                      {formatDate(caseMetadata.statusUpdated, 'HH:mm d MMM y')}
                    </span>
                  )}
                </span>
              );
            })()
          ) : (
            <EmptyValue />
          )}
        </InfoField>

        <InfoField label="Assigned to" looseGap>
          {caseMetadata?.assignedTo ? (
            <span className="flex items-center gap-2" data-assigned-to-user-id={caseMetadata.assignedTo.userId}>
              <UserAvatar name={caseMetadata.assignedTo.userName} size={24} fontSize={10} />
              <span>{caseMetadata.assignedTo.userName}</span>
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <UserAvatar unassigned size={24} />
              <span className="text-fletchers-gray-200 italic">Unassigned</span>
            </span>
          )}
        </InfoField>

        <InfoField label="Created by" looseGap>
          {caseMetadata?.createdBy ? (
            <span className="flex items-center gap-2" data-created-by-user-id={caseMetadata.createdBy.userId}>
              <UserAvatar name={caseMetadata.createdBy.userName} size={24} fontSize={10} />
              <span>{caseMetadata.createdBy.userName}</span>
            </span>
          ) : (
            <EmptyValue />
          )}
        </InfoField>

        {caseMetadata?.cloneOf && (
          <>
            <hr className="border-fletchers-gray-110" />

            <InfoField label="Cloned from">
              <button
                type="button"
                onClick={() =>
                  caseMetadata.cloneOf && openNewTab(`/${AppRoutes.CaseList}/${caseMetadata.cloneOf.caseId}`)
                }
                className="text-fletchers-green-500 hover:text-fletchers-green-800 cursor-pointer text-left text-[14px]/[20px]"
              >
                {caseMetadata.cloneOf.caseReference}
              </button>
            </InfoField>
          </>
        )}

        {/* Case background documents — read-only mirror of the section on
            the Create/Edit sidebar. Same tile grid, but with `canEdit`
            false so no Upload tile / trash button appears. Click-to-
            download uses the shared handler above. Divider only shown
            when the case actually has docs, so the sidebar doesn't end
            on a trailing line for cases without any. */}
        {caseBackgroundDocs.length > 0 && (
          <>
            <hr className="border-fletchers-gray-110" />
            <div className="flex flex-col gap-2">
              <h2 className="text-fletchers-gray-200 text-[14px]/[20px] font-[500]">Case background</h2>
              <CaseBackgroundSection
                documents={caseBackgroundDocs}
                canEdit={false}
                onUploadClick={() => {}}
                onDelete={() => {}}
                onFilesSelected={() => {}}
                onDownload={handleAllegationDownload}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};
